import React, { useEffect, useState, useCallback, useMemo } from "react";
import ReceiptModal from "./ReceiptModal";
import * as XLSX from "xlsx";
import Receipt from "./Receipt";
import PatientEditModal from "./PatientEditModal";
import Select from "react-select";
import { customFetch } from '../apiClient';

// ── Summary Card Component ──────────────────────────────────────
const SummaryCard = ({ icon, label, value, accent, onClick, isLoading }) => (
  <div
    className={`sum-card ${onClick ? 'clickable' : ''}`}
    style={{ "--accent": accent }}
    onClick={!isLoading && onClick ? onClick : undefined}
  >
    <div className="sum-icon" style={{ color: accent }}>
      {isLoading
        ? <div className="spinner-border spinner-border-sm" style={{ borderWidth: '2px' }}></div>
        : icon}
    </div>
    <div className="sum-body">
      <div className="sum-val" style={{ color: accent }}>
        ₹{Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
      </div>
      <div className="sum-label">{label}</div>
    </div>
    {onClick && !isLoading && (
      <div className="sum-click-hint">
        <i className="bi bi-arrow-up-right-circle"></i>
      </div>
    )}
    <div className="sum-glow" />
  </div>
);

// ── Helper: Convert discount percent → rupees ──────────────────
// DB mein discount percent hai (e.g. 10 means 10%), consultFee se calculate karo
const calcDiscountRs = (item) => {
  const consultFee = Number(item.consultFee || 0);
  const discountPct = Number(item.discount || 0);
  // Agar value 0-100 range mein ho toh percent maan lo
  if (discountPct > 0 && discountPct <= 100) {
    return (consultFee * discountPct) / 100;
  }
  // Agar directly rupees mein save hai (100 se zyada) toh as-is
  return discountPct;
};

const OpdList = () => {
  const [opdList, setOpdList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [useDateFilter, setUseDateFilter] = useState(true);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);

  const [fromDate, setFromDate] = useState(new Date().toISOString().split("T")[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);
  const [searchText, setSearchText] = useState("");
  const [searchType, setSearchType] = useState("");
  const [departments, setDepartments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [packages, setPackages] = useState([]);

  const [receiptData, setReceiptData] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [showReceiptPopup, setShowReceiptPopup] = useState(false);
  const [receiptPopupData, setReceiptPopupData] = useState(null);

  const [editData, setEditData] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const [companyDetails, setCompanyDetails] = useState(null);
  const [loggedInUser, setLoggedInUser] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  const [summaryData, setSummaryData] = useState(null);

  // Convert to IPD Modal States
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [recordToConvert, setRecordToConvert] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [converting, setConverting] = useState(false);

  // Report Modal States
  const [reportLoading, setReportLoading] = useState(null);
  const [showDuesModal, setShowDuesModal] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [authFilter, setAuthFilter] = useState(null);
  const [discountAuthoritiesList, setDiscountAuthoritiesList] = useState([]);


// 🚀 Fetch Discount Authorities for Modal Dropdown
  const fetchDiscountAuthorities = useCallback(async () => {
    try {
      const res = await customFetch(`/DiscountAuthDt`);
      if (res.ok) {
        const data = await res.json();
        // 👇 YAHAN CHANGE KRO
        setDiscountAuthoritiesList(data.map(a => ({ 
            value: a.authority, // Value mein name pass karna hai taaki filter match ho sake
            label: `${a.type}: ${a.authority}`, 
            authName: a.authority 
        })));
      }
    } catch (err) { console.error(err.message); }
  }, []);


  const searchOptions = [
    { value: "", label: "All Fields" },
    { value: "name", label: "Name" },
    { value: "mobile", label: "Mobile No" },
    { value: "uhid", label: "UHID No" },
    { value: "srno", label: "Last 4 Digits" },
    { value: "id", label: "ID" }
  ];

  // Fetch Packages
  useEffect(() => {
    const loadPackages = async () => {
      try {
        const res = await customFetch(`/PackageMaster/active`);
        if (res.ok) {
          const data = await res.json();
          setPackages(data.map(p => ({
            value: p.id,
            label: `${p.packageName} (₹${p.amount})`,
            originalData: p
          })));
        }
      } catch (err) { console.error("Failed to load packages", err); }
    };
    loadPackages();
  }, []);

  // Export Logic
  useEffect(() => {
    const handleExportEvent = async () => {
      if (!opdList || opdList.length === 0) {
        alert("No records found to export!");
        return;
      }
      const exportData = opdList.map((item, index) => ({
        "Sr No": index + 1,
        "OPD ID": item.opD_SRNO || item.opdId,
        "UHID": item.uhidNo,
        "Patient Name": item.name,
        "Age": item.age,
        "Gender": item.gender,
        "Doctor": item.doctorName,
        "Department": item.departmentName,
        "OPD Date": formatDateToDDMMYYYY(item.opdDate),
        "Mobile": item.mobileNo,
        "Address": item.address,
        "Consult Fee": item.consultFee,
        "Discount %": item.discount,
        "Discount (₹)": calcDiscountRs(item).toFixed(2),
        "Payable": item.payable,
        "Status": item.syncSts ? "Converted to IPD" : "Active"
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "OPD_Patients");

      const defaultFileName = `OPD_List_${new Date().toISOString().split("T")[0]}.xlsx`;
      try {
        if (window.showSaveFilePicker) {
          const fileHandle = await window.showSaveFilePicker({
            suggestedName: defaultFileName,
            types: [{ description: 'Excel Workbook', accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] } }],
          });
          const writable = await fileHandle.createWritable();
          const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
          await writable.write(excelBuffer);
          await writable.close();
        } else {
          XLSX.writeFile(workbook, defaultFileName);
        }
      } catch (error) {
        if (error.name !== 'AbortError') alert("Error saving file!");
      }
    };
    window.addEventListener("exportData", handleExportEvent);
    return () => window.removeEventListener("exportData", handleExportEvent);
  }, [opdList]);

  // Main Fetch Logic
  const buildFetchUrl = (page, rows) => {
    let url = `/Opds/list?page=${page}&pageSize=${rows}&useDateFilter=${useDateFilter}&`;
    if (useDateFilter) {
      if (fromDate) url += `from=${fromDate}&`;
      if (toDate) url += `to=${toDate}&`;
    }
    if (searchText) {
      url += `search=${encodeURIComponent(searchText)}&`;
      if (searchType) url += `searchType=${searchType}&`;
    }
    if (selectedDepartment) url += `departmentId=${selectedDepartment.value}&`;
    if (selectedDoctor) url += `doctorId=${selectedDoctor.value}&`;
    return url.replace(/[&?]$/, "");
  };

  const fetchOpdList = async (page = currentPage, rows = rowsPerPage) => {
    try {
      setLoading(true); setError("");
      const res = await customFetch(buildFetchUrl(page, rows));
      if (!res.ok) throw new Error("Failed to fetch OPD data");
      const data = await res.json();
      setOpdList(data.data || data || []);
      setTotalRecords(data.totalRecords || data.length || 0);
      if (data.summary) setSummaryData(data.summary);
      setCurrentPage(page);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  // Fetch Report Data (Dues / Discount)
  const fetchReportData = async (type) => {
    try {
      setReportLoading(type);
      const res = await customFetch(buildFetchUrl(1, 10000));
      if (!res.ok) throw new Error("Failed to fetch report data");
      const data = await res.json();
      const list = data.data || data || [];

      if (type === 'DUES') {
        const duesList = list.filter(item => Number(item.dues) > 0);
        setReportData(duesList);
        setShowDuesModal(true);
      } else if (type === 'DISCOUNT') {
        const discountList = list.filter(item => Number(item.discount) > 0);
        setReportData(discountList);
        setAuthFilter(null);
        setShowDiscountModal(true);
      }
    } catch (err) {
      alert("Error fetching report data.");
    } finally {
      setReportLoading(null);
    }
  };

  // Convert to IPD
  const openConvertModal = (opdRecord) => { setRecordToConvert(opdRecord); setSelectedPackage(null); setShowConvertModal(true); };
  const executeConvert = async () => {
    if (!recordToConvert) return;
    try {
      setConverting(true);
      const payload = {
        PackageId: selectedPackage ? selectedPackage.value : null,
        PackageName: selectedPackage ? selectedPackage.originalData.packageName : null,
        PackageAmount: selectedPackage ? selectedPackage.originalData.amount : null,
      };
      const res = await customFetch(`/Opds/convert-to-ipd/${recordToConvert.opdId}`, { method: "POST", body: JSON.stringify(payload) });
      if (!res.ok) throw new Error(await res.text() || "Failed to convert to IPD");
      const result = await res.json();
      alert(`✅ Success! Patient converted to IPD.\nUHID: ${result.uhidNo}\nIPD ID: ${result.ipdId}`);
      setShowConvertModal(false);
      fetchOpdList(currentPage, rowsPerPage || 10);
    } catch (err) { alert(`❌ Error: ${err.message}`); }
    finally { setConverting(false); }
  };

  const handleSearchTypeChange = (selectedOption) => { setSearchType(selectedOption ? selectedOption.value : ""); handleFilterChange(); };
  const handleFilterChange = () => { setCurrentPage(1); };

const fetchDepartments = useCallback(async () => {
  try {
    const res = await customFetch(`/Departments`);
    if (res.ok) setDepartments((await res.json()).map(d => ({ value: d.srno, label: d.departmentName, isActive: d.isActive }))); // 👈 YAHAN isActive ADD KIYA HAI
  } catch (err) { console.error(err.message); }
}, []);

  const fetchDoctors = useCallback(async () => {
    try {
      const res = await customFetch(`/Employees/doctors`);
      if (res.ok) setDoctors((await res.json()).map(d => ({ value: d.id, label: d.name })));
    } catch (err) { console.error(err.message); }
  }, []);

  useEffect(() => { fetchDepartments(); fetchDoctors(); fetchDiscountAuthorities();}, [fetchDepartments, fetchDoctors, fetchDiscountAuthorities]);
  useEffect(() => { fetchOpdList(1, rowsPerPage || 10); }, [fromDate, toDate, searchText, searchType, useDateFilter, selectedDepartment, selectedDoctor]);

  useEffect(() => {
    const hospitalId = sessionStorage.getItem("hospitalId");
    setLoggedInUser(sessionStorage.getItem("LoginID"));
    if (hospitalId) customFetch(`/Company/${hospitalId}`).then((res) => res.json()).then(setCompanyDetails).catch(console.error);
  }, []);

  const handleFromDateChange = (e) => { setFromDate(e.target.value); handleFilterChange(); };
  const handleToDateChange = (e) => { setToDate(e.target.value); handleFilterChange(); };
  const handleSearchChange = (e) => { setSearchText(e.target.value); handleFilterChange(); };
  const handleRowsPerPageChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue === '') { setRowsPerPage(''); setCurrentPage(1); return; }
    let value = parseInt(rawValue);
    if (isNaN(value) || value < 1) value = 10;
    if (value > 100) value = 100;
    setRowsPerPage(value); setCurrentPage(1);
  };
  const handleRowsPerPageBlur = (e) => { if (e.target.value === '' || !rowsPerPage) setRowsPerPage(10); };
  const handlePageChange = (page) => { fetchOpdList(page, rowsPerPage || 10); };

  const effectiveRows = rowsPerPage || 10;
  const totalPages = Math.ceil(totalRecords / effectiveRows);

  const handleEdit = (record) => { setEditData(record); setShowEditModal(true); };
  const handlePreview = (record) => { setReceiptData(record); setShowReceipt(true); };
  const handleOpenReceiptPopup = (record) => { setReceiptPopupData(record); setShowReceiptPopup(true); };
  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this record?")) {
      customFetch(`/Opds/${id}`, { method: "DELETE" })
        .then((res) => {
          if (res.ok) { alert("Record deleted successfully"); fetchOpdList(currentPage, rowsPerPage || 10); }
          else alert("Failed to delete record");
        }).catch(() => alert("Network error while deleting"));
    }
  };

  function formatDateToDDMMYYYY(dateString) {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date)) return "";
    return `${("0" + date.getDate()).slice(-2)}-${("0" + (date.getMonth() + 1)).slice(-2)}-${date.getFullYear()}`;
  }

  // // ── Discount Modal: Authority Options (unique list from reportData) ──
  // const discountAuthOptions = useMemo(() => {
  //   const auths = reportData
  //     .map(item => item.discountAuthName)
  //     .filter(Boolean);
  //   const unique = Array.from(new Set(auths));
  //   return unique.map(a => ({ value: a, label: a }));
  // }, [reportData]);

  // ── Discount Modal: Filtered data based on selected authority ──
  const filteredDiscountData = useMemo(() => {
    if (!authFilter) return reportData;
    return reportData.filter(item => item.discountAuthName === authFilter.value);
  }, [reportData, authFilter]);

  // ── Discount Modal: Total in ₹ ──
  const totalDiscountRs = useMemo(() =>
    filteredDiscountData.reduce((sum, item) => sum + calcDiscountRs(item), 0),
    [filteredDiscountData]
  );

  const selectStyles = {
    control: (base, state) => ({
      ...base, minHeight: '28px', height: '28px', fontSize: '11px', borderRadius: '4px',
      borderColor: state.isFocused ? 'var(--theme-color, #0088cc)' : '#cbd5e0',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(0,0,0,0.05)' : 'none',
      '&:hover': { borderColor: 'var(--theme-color, #0088cc)' }
    }),
    valueContainer: (base) => ({ ...base, padding: '0 6px' }),
    input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: '11px' }),
    indicatorsContainer: (base) => ({ ...base, height: '28px' }),
    dropdownIndicator: (base) => ({ ...base, padding: '2px 4px' }),
    clearIndicator: (base) => ({ ...base, padding: '2px 4px' }),
    menu: (base) => ({ ...base, zIndex: 9999, borderRadius: '4px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', border: '1px solid #e0e6ed', fontSize: '11px' }),
    option: (base, state) => ({
      ...base, fontSize: '11px', padding: '5px 8px',
      backgroundColor: state.isSelected ? 'var(--theme-color, #0088cc)' : state.isFocused ? '#e0f2fe' : 'white',
      color: state.isSelected ? 'white' : '#2c3e50', cursor: 'pointer',
    }),
  };

  const renderPagination = () => {
    if (!effectiveRows || totalRecords === 0) return null;
    const pageNumbers = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    if (endPage - startPage + 1 < maxVisiblePages) startPage = Math.max(1, endPage - maxVisiblePages + 1);

    if (currentPage > 1) pageNumbers.push(<button key="prev" className="pagination-btn pagination-btn-nav" onClick={() => handlePageChange(currentPage - 1)}><i className="bi bi-chevron-left"></i> Previous</button>);
    for (let i = startPage; i <= endPage; i++) pageNumbers.push(<button key={i} className={`pagination-btn ${currentPage === i ? 'active' : ''}`} onClick={() => handlePageChange(i)}>{i}</button>);
    if (currentPage < totalPages) pageNumbers.push(<button key="next" className="pagination-btn pagination-btn-nav" onClick={() => handlePageChange(currentPage + 1)}>Next <i className="bi bi-chevron-right"></i></button>);
    return pageNumbers;
  };

  return (
    <div className="opd-list-container">
      <div className="opd-list-card">

        {/* ── Header ── */}
        <div className="opd-header">
          <div className="header-left">
            <div className="header-icon"><i className="bi bi-person-lines-fill"></i></div>
            <div className="header-text">
              <h5>OPD Patient List</h5>
              <p>View, manage, and track outpatient department records</p>
            </div>
          </div>
          <div className="header-stats">
            <div className="stat-item">
              <i className="bi bi-people-fill"></i>
              <div className="stat-info">
                <span className="stat-value">{totalRecords}</span>
                <span className="stat-label">Total Patients</span>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert-custom alert-danger slide-in">
            <i className="bi bi-exclamation-triangle-fill"></i><span>{error}</span>
            <button className="alert-close" onClick={() => setError("")}><i className="bi bi-x"></i></button>
          </div>
        )}

        <div className="opd-body">

          {/* ── Filters ── */}
          <div className="filters-section">
            <div className="section-header">
              <i className="bi bi-funnel"></i><span>Filters &amp; Search</span>
            </div>
            <div className="filters-content">
              <div className="date-check-row">
                <input type="checkbox" id="dateFilterCheck" checked={useDateFilter} onChange={(e) => { setUseDateFilter(e.target.checked); handleFilterChange(); }} />
                <label htmlFor="dateFilterCheck">Apply Date Filter</label>
              </div>
              <div className="filter-row">
                <div className="filter-group">
                  <label><i className="bi bi-calendar-event"></i> From Date</label>
                  <input type="date" className="form-control-custom" value={fromDate} onChange={handleFromDateChange} disabled={!useDateFilter} style={{ opacity: !useDateFilter ? 0.5 : 1 }} />
                </div>
                <div className="filter-group">
                  <label><i className="bi bi-calendar-check"></i> To Date</label>
                  <input type="date" className="form-control-custom" value={toDate} onChange={handleToDateChange} disabled={!useDateFilter} style={{ opacity: !useDateFilter ? 0.5 : 1 }} />
                </div>
                <div className="filter-group">
                  <label><i className="bi bi-building"></i> Department</label>
              <Select 
  options={departments.filter(d => d.isActive === true)} // 👈 KEVAl YAHAN INLINE FILTER LAGAYEIN
  value={selectedDepartment}
  onChange={(option) => { setSelectedDepartment(option); handleFilterChange(); }}
  styles={selectStyles}
  placeholder="All Depts"
  isClearable={true}
  menuPortalTarget={document.body}
/>
                </div>
                <div className="filter-group">
                  <label><i className="bi bi-person-badge"></i> Doctor</label>
                  <Select options={doctors} value={selectedDoctor} onChange={(o) => { setSelectedDoctor(o); handleFilterChange(); }} styles={selectStyles} placeholder="All Doctors" isClearable menuPortalTarget={document.body} />
                </div>
                <div className="filter-group">
                  <label><i className="bi bi-search"></i> Search By</label>
                  <Select options={searchOptions} value={searchOptions.find(opt => opt.value === searchType)} onChange={handleSearchTypeChange} styles={selectStyles} placeholder="All Fields" isSearchable={false} menuPortalTarget={document.body} />
                </div>
                <div className="filter-group filter-group-flex2">
                  <label>
                    <i className="bi bi-filter"></i> Search
                    {searchType ? ` by ${searchType === 'name' ? 'Name' : searchType === 'mobile' ? 'Mobile' : searchType === 'uhid' ? 'UHID' : searchType === 'srno' ? 'Last 4 Digits' : searchType === 'id' ? 'ID' : ''}` : ''}
                  </label>
                  <input
                    type="text" className="form-control-custom" value={searchText} onChange={handleSearchChange}
                    placeholder={searchType === "" ? "Search all fields..." : searchType === "name" ? "Enter patient name" : searchType === "mobile" ? "Enter mobile number" : searchType === "uhid" ? "Enter UHID" : searchType === "srno" ? "Enter last 4 digits" : searchType === "id" ? "Enter ID" : "Search..."}
                  />
                </div>
                <div className="filter-group filter-group-rows">
                  <label><i className="bi bi-table"></i> Rows</label>
                  <input type="number" className="form-control-custom" value={rowsPerPage} placeholder="10" min="1" max="100" onChange={handleRowsPerPageChange} onBlur={handleRowsPerPageBlur} />
                </div>
                <div className="filter-group filter-group-btn">
                  <label>&nbsp;</label>
                  <button className="btn-refresh" onClick={() => fetchOpdList(1, rowsPerPage || 10)} disabled={loading}>
                    <i className={`bi bi-arrow-clockwise ${loading ? 'spinning' : ''}`}></i> Refresh
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Financial Summary Cards */}
          {summaryData && (
            <div className="cards-grid">
              <SummaryCard icon={<i className="bi bi-cash-stack"></i>} label="Total Consult Fee" value={summaryData.totalConsultFee} accent="#0ea5e9" />
              <SummaryCard
                icon={<i className="bi bi-scissors"></i>} label="Total Discount"
                value={summaryData.totalDiscount} accent="#7c3aed"
                onClick={() => fetchReportData('DISCOUNT')}
                isLoading={reportLoading === 'DISCOUNT'}
              />
              <SummaryCard icon={<i className="bi bi-calculator"></i>} label="Total Payable" value={summaryData.totalPayable} accent="#059669" />
              <SummaryCard icon={<i className="bi bi-wallet2"></i>} label="Total Received" value={summaryData.totalReceived} accent="#d97706" />
              <SummaryCard
                icon={<i className="bi bi-exclamation-circle"></i>} label="Total Dues"
                value={summaryData.totalDue} accent="#dc2626"
                onClick={() => fetchReportData('DUES')}
                isLoading={reportLoading === 'DUES'}
              />
            </div>
          )}

          {/* Records Info */}
          <div className="records-info">
            <div className="info-left">
              <i className="bi bi-info-circle"></i>
              <span>Showing {((currentPage - 1) * effectiveRows) + 1} to {Math.min(currentPage * effectiveRows, totalRecords)} of {totalRecords} entries</span>
            </div>
            <div className="info-right">
              {totalPages > 0 && <span className="page-info"><i className="bi bi-file-earmark-text"></i> Page {currentPage} of {totalPages}</span>}
            </div>
          </div>

          {/* Table */}
          <div className="table-container">
            {loading ? (
              <div className="loading-state"><div className="spinner"></div><p>Loading OPD records...</p></div>
            ) : opdList.length === 0 ? (
              <div className="empty-state"><i className="bi bi-inbox"></i><h6>No OPD Records Found</h6><p>No patients match your search criteria</p></div>
            ) : (
              <table className="opd-table">
                <thead>
                  <tr>
                    <th><i className="bi bi-prescription"></i> Rx</th>
                    <th><i className="bi bi-file-earmark-medical"></i> Slip</th>
                    <th><i className="bi bi-hash"></i> ID</th>
                    <th><i className="bi bi-card-heading"></i> UHID</th>
                    <th><i className="bi bi-person"></i> Name</th>
                    <th><i className="bi bi-gender-ambiguous"></i> Age/Gen</th>
                    <th><i className="bi bi-person-badge"></i> Doctor</th>
                    <th><i className="bi bi-building"></i> Dept</th>
                    <th><i className="bi bi-calendar"></i> Date</th>
                    <th><i className="bi bi-geo-alt"></i> Address</th>
                    <th><i className="bi bi-phone"></i> Mobile</th>
                    <th><i className="bi bi-arrow-right-circle"></i> IPD</th>
                    <th><i className="bi bi-tools"></i> Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {opdList.map((o) => (
                    <tr key={o.opdId} className={o.syncSts ? 'row-converted' : ''}>
                      <td className="action-cell"><button className="btn-icon btn-prescription" onClick={() => handleOpenReceiptPopup(o)} title="View Prescription"><i className="bi bi-eye-fill"></i></button></td>
                      <td className="action-cell"><button className="btn-icon btn-regslip" onClick={() => handlePreview(o)} title="View RegSlip"><i className="bi bi-file-text"></i></button></td>
                      <td title={o.opD_SRNO}><span className="id-badge">{o.opD_SRNO}</span></td>
                      <td title={o.uhidNo}><span className="uhid-badge">{o.uhidNo}</span></td>
                      <td title={o.name}><div className="patient-name"><i className="bi bi-person-circle"></i> {o.name}</div></td>
                      <td title={`${o.age} / ${o.gender}`}><span className="age-gender">{o.age} / {o.gender?.charAt(0)}</span></td>
                      <td title={o.doctorName}><div className="doctor-name"><i className="bi bi-person-fill-check"></i> {o.doctorName}</div></td>
                      <td title={o.departmentName}>{o.departmentName}</td>
                      <td><span className="date-badge">{formatDateToDDMMYYYY(o.opdDate)}</span></td>
                      <td className="address-cell" title={o.address}>{o.address}</td>
                      <td><span className="mobile-badge"><i className="bi bi-telephone"></i> {o.mobileNo}</span></td>
                      <td className="action-cell">
                        {o.syncSts
                          ? <span className="badge-converted"><i className="bi bi-check-circle-fill"></i> Done</span>
                          : <button className="btn-icon btn-convert" onClick={() => openConvertModal(o)} title="Convert to IPD"><i className="bi bi-arrow-right-circle-fill"></i></button>}
                      </td>
                      <td className="action-cell">
                        <div className="action-buttons">
                          <button className="btn-icon btn-edit" onClick={() => handleEdit(o)} title="Edit"><i className="bi bi-pencil-square"></i></button>
                          <button className="btn-icon btn-delete" onClick={() => handleDelete(o.opdId)} title="Delete"><i className="bi bi-trash"></i></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && <div className="pagination-section">{renderPagination()}</div>}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════
          MODAL: DUES REPORT — Fixed alignment + Modern UI
      ═══════════════════════════════════════════════════════════ */}
      {showDuesModal && (
        <div className="modal-backdrop" onClick={() => setShowDuesModal(false)}>
          <div className="report-modal" onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="rmodal-header rmodal-header-red">
              <div className="rmodal-header-left">
                <div className="rmodal-icon-wrap rmodal-icon-red">
                  <i className="bi bi-exclamation-octagon-fill"></i>
                </div>
                <div>
                  <h6>Outstanding Dues</h6>
                  <p>{reportData.length} patient{reportData.length !== 1 ? 's' : ''} with pending dues</p>
                </div>
              </div>
              <button className="rmodal-close" onClick={() => setShowDuesModal(false)}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            {/* Table */}
            <div className="rmodal-table-wrap">
              <table className="rmodal-table">
                <thead>
                  <tr>
                    <th style={{ width: '8%' }}>#</th>
                    <th style={{ width: '30%' }}>Patient Name</th>
                    <th style={{ width: '25%' }}>UHID</th>
                    <th style={{ width: '20%' }}>Mobile</th>
                    <th style={{ width: '17%', textAlign: 'right' }}>Due (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="rmodal-empty">
                        <i className="bi bi-check-circle text-success"></i>
                        <span>No dues found for selected filters</span>
                      </td>
                    </tr>
                  ) : (
                    reportData.map((item, idx) => (
                      <tr key={item.opdId}>
                        <td className="rmodal-sr">{idx + 1}</td>
                        <td>
                          <div className="rmodal-patient">
                            <i className="bi bi-person-circle"></i>
                            <span>{item.name}</span>
                          </div>
                        </td>
                        <td><span className="rmodal-uhid">{item.uhidNo}</span></td>
                        <td className="rmodal-mobile">
                          <i className="bi bi-telephone"></i> {item.mobileNo}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <span className="rmodal-badge rmodal-badge-red">
                            ₹{Number(item.dues).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="rmodal-footer">
              <span className="rmodal-footer-note">
                <i className="bi bi-info-circle"></i> Showing records for current filter
              </span>
              <div className="rmodal-total rmodal-total-red">
                <span>Total Outstanding</span>
                <strong>
                  ₹{reportData.reduce((sum, i) => sum + Number(i.dues || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </strong>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          MODAL: DISCOUNT REPORT — Fixed alignment + Authority column + % to ₹ + Modern UI
      ═══════════════════════════════════════════════════════════ */}
      {showDiscountModal && (
        <div className="modal-backdrop" onClick={() => setShowDiscountModal(false)}>
          <div className="report-modal" onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="rmodal-header rmodal-header-purple">
              <div className="rmodal-header-left">
                <div className="rmodal-icon-wrap rmodal-icon-purple">
                  <i className="bi bi-scissors"></i>
                </div>
                <div>
                  <h6>Discount Report</h6>
                  <p>{reportData.length} patient{reportData.length !== 1 ? 's' : ''} with discounts applied</p>
                </div>
              </div>
              <button className="rmodal-close" onClick={() => setShowDiscountModal(false)}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            {/* Filter Bar */}
            <div className="rmodal-filter-bar">
              <div className="rmodal-filter-left">
                <i className="bi bi-funnel" style={{ color: '#7c3aed', fontSize: '12px' }}></i>
                <span>Filter by Authority</span>
              </div>
              <div style={{ width: '220px' }}>
               <Select 
  options={discountAuthoritiesList} 
  value={authFilter} 
  onChange={setAuthFilter} 
  isClearable 
  placeholder="All Authorities"
  styles={{
    control: base => ({...base, minHeight: '28px', height: '28px', fontSize: '11px', borderColor: '#cbd5e0'}),
    valueContainer: base => ({...base, padding: '0 6px'}),
    indicatorsContainer: base => ({...base, height: '28px'}),
    option: (base, state) => ({...base, fontSize: '11px', backgroundColor: state.isFocused ? '#f1f5f9' : 'white', color: '#1e293b'}),
    // 👇 YEH DONO LINES ADD KARO TAAKI DROPDOWN MODAL KE UPAR DIKHE
    menuPortal: base => ({ ...base, zIndex: 99999 }), 
    menu: base => ({ ...base, zIndex: 99999 })
  }}
  menuPortalTarget={document.body}
/>
              </div>
              {authFilter && (
                <span className="rmodal-filter-active">
                  <i className="bi bi-check-circle-fill"></i>
                  {filteredDiscountData.length} record{filteredDiscountData.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>

            {/* Table */}
            <div className="rmodal-table-wrap">
              <table className="rmodal-table">
                <thead>
                  <tr>
                    <th style={{ width: '7%' }}>#</th>
                    <th style={{ width: '26%' }}>Patient Name</th>
                    <th style={{ width: '20%' }}>UHID</th>
                    <th style={{ width: '22%' }}>Authority</th>
                    <th style={{ width: '12%', textAlign: 'right' }}>%</th>
                    <th style={{ width: '13%', textAlign: 'right' }}>Discount (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDiscountData.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="rmodal-empty">
                        <i className="bi bi-search"></i>
                        <span>No discounts found for selected authority</span>
                      </td>
                    </tr>
                  ) : (
                    filteredDiscountData.map((item, idx) => {
                      const discountRs = calcDiscountRs(item);
                      return (
                        <tr key={item.opdId}>
                          <td className="rmodal-sr">{idx + 1}</td>
                          <td>
                            <div className="rmodal-patient">
                              <i className="bi bi-person-circle"></i>
                              <span>{item.name}</span>
                            </div>
                          </td>
                          <td><span className="rmodal-uhid">{item.uhidNo}</span></td>
                          <td>
                            {item.discountAuthName
                              ? <span className="rmodal-auth-badge">{item.discountAuthName}</span>
                              : <span className="rmodal-auth-unknown">Not Set</span>
                            }
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <span className="rmodal-pct">{Number(item.discount || 0).toFixed(1)}%</span>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <span className="rmodal-badge rmodal-badge-purple">
                              ₹{discountRs.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="rmodal-footer">
              <span className="rmodal-footer-note">
                <i className="bi bi-info-circle"></i>
                {authFilter ? `Filtered by: ${authFilter.label}` : 'Showing all discount records'}
              </span>
              <div className="rmodal-total rmodal-total-purple">
                <span>Total Discount</span>
                <strong>
                  ₹{totalDiscountRs.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </strong>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          MODAL: CONVERT TO IPD
      ═══════════════════════════════════════════════════════════ */}
      {showConvertModal && recordToConvert && (
        <div className="modal-backdrop">
          <div className="convert-modal">
            <div className="modal-header">
              <h6><i className="bi bi-box-arrow-in-right me-2"></i> Convert to IPD</h6>
              <button className="close-btn" onClick={() => setShowConvertModal(false)}><i className="bi bi-x-lg"></i></button>
            </div>
            <div className="modal-body">
              <div className="patient-summary">
                <h6>{recordToConvert.name}</h6>
                <div className="details">
                  <span><i className="bi bi-person-vcard text-primary"></i> {recordToConvert.uhidNo}</span>
                  <span><i className="bi bi-telephone text-primary"></i> {recordToConvert.mobileNo}</span>
                </div>
              </div>
              <div className="form-group mb-2">
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#1e293b', marginBottom: '6px', display: 'block' }}>
                  <i className="bi bi-box-seam text-primary me-1"></i> Assign IPD Package (Optional)
                </label>
                <Select
                  options={packages} value={selectedPackage} onChange={(o) => setSelectedPackage(o)}
                  placeholder="Select a package or leave empty..." isClearable menuPortalTarget={document.body} menuPosition="fixed"
                  styles={{
                    control: (base, state) => ({ ...base, borderColor: state.isFocused ? '#0088cc' : '#cbd5e0', boxShadow: state.isFocused ? '0 0 0 2px rgba(0,136,204,0.1)' : 'none', minHeight: '32px', fontSize: '12px' }),
                    menuPortal: base => ({ ...base, zIndex: 999999 }),
                    menu: base => ({ ...base, zIndex: 999999, fontSize: '12px', marginTop: '2px' }),
                    option: (base, state) => ({ ...base, fontSize: '12px', backgroundColor: state.isSelected ? '#0088cc' : state.isFocused ? '#e0f2fe' : 'white', color: state.isSelected ? 'white' : '#333' })
                  }}
                />
                <small style={{ display: 'block', marginTop: '8px', fontSize: '10.5px', color: '#64748b' }}>
                  <i className="bi bi-info-circle me-1"></i> If no package is selected, patient will be admitted under standard billing.
                </small>
              </div>
            </div>
            <div className="modal-footer" style={{ padding: '15px 20px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button className="btn-cancel-modal" onClick={() => setShowConvertModal(false)}>Cancel</button>
              <button className="btn-confirm-modal" onClick={executeConvert} disabled={converting}>
                {converting ? <><span className="spinner-border spinner-border-sm"></span> Converting...</> : <><i className="bi bi-check-circle"></i> Confirm Admission</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Sub-Modals ── */}
      {companyDetails && <ReceiptModal show={showReceipt} data={receiptData} companyDetails={companyDetails} loggedInUser={loggedInUser} onClose={() => setShowReceipt(false)} onPrint={() => window.print()} />}
      {companyDetails && showReceiptPopup && <Receipt show={showReceiptPopup} data={receiptPopupData} companyDetails={companyDetails} loggedInUser={loggedInUser} onClose={() => setShowReceiptPopup(false)} />}
      {showEditModal && editData && <PatientEditModal entityType="OPD" patientData={editData} departments={departments} doctors={doctors} onClose={() => setShowEditModal(false)} onUpdate={() => { fetchOpdList(currentPage, rowsPerPage || 10); setShowEditModal(false); }} />}

      <style jsx>{`
        .opd-list-container { padding: 0; }
        .opd-list-card { background: white; border-radius: 10px; box-shadow: 0 2px 15px rgba(0,136,204,0.08); border: 1px solid #e0e6ed; overflow: hidden; }

        @keyframes slideIn { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }
        .slide-in { animation: slideIn 0.3s ease; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .spinning { animation: spin 0.8s linear infinite; display:inline-block; }

        /* ── Modal Backdrop ── */
        .modal-backdrop { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.55); display: flex; justify-content: center; align-items: center; z-index: 1050; backdrop-filter: blur(4px); }

        /* ════════════════════════════════════════════
           REPORT MODAL — Modern Design
        ════════════════════════════════════════════ */
        .report-modal {
          width: 95%;
          max-width: 860px;
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.18);
          animation: slideIn 0.25s ease;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          max-height: 90vh;
        }

        /* Report Modal Header */
        .rmodal-header {
          padding: 16px 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-shrink: 0;
        }
        .rmodal-header-red { background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%); }
        .rmodal-header-purple { background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%); }

        .rmodal-header-left { display: flex; align-items: center; gap: 12px; }
        .rmodal-icon-wrap {
          width: 36px; height: 36px; border-radius: 10px;
          background: rgba(255,255,255,0.2); display: flex; align-items: center;
          justify-content: center; font-size: 16px; color: white; flex-shrink: 0;
        }
        .rmodal-header h6 { margin: 0; font-size: 15px; font-weight: 700; color: white; line-height: 1.2; }
        .rmodal-header p { margin: 0; font-size: 11px; color: rgba(255,255,255,0.8); line-height: 1.2; margin-top: 2px; }

        .rmodal-close {
          background: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.25);
          color: white; width: 30px; height: 30px; border-radius: 8px;
          cursor: pointer; font-size: 13px; display: flex; align-items: center;
          justify-content: center; transition: background 0.2s; flex-shrink: 0;
        }
        .rmodal-close:hover { background: rgba(255,255,255,0.3); }

        /* Filter Bar */
        .rmodal-filter-bar {
          display: flex; align-items: center; gap: 10px;
          padding: 10px 16px; background: #f8fafc;
          border-bottom: 1px solid #e2e8f0; flex-shrink: 0; flex-wrap: wrap;
        }
        .rmodal-filter-left { display: flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 600; color: #475569; }
        .rmodal-filter-active { font-size: 10px; color: #7c3aed; background: #f5f3ff; padding: 2px 8px; border-radius: 20px; border: 1px solid #ddd6fe; display: flex; align-items: center; gap: 4px; }
        .rmodal-filter-active i { font-size: 10px; }

        /* Table Wrapper — FIXED SCROLL */
        .rmodal-table-wrap {
          overflow-y: auto;
          flex: 1;
          min-height: 0;
        }

        /* Table — FIXED LAYOUT for proper alignment */
        .rmodal-table {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed; /* KEY FIX: columns won't shift */
        }

        .rmodal-table thead th {
          padding: 8px 10px;
          font-size: 10px;
          font-weight: 600;
          color: #64748b;
          text-align: left;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          position: sticky;
          top: 0;
          z-index: 2;
          text-transform: uppercase;
          letter-spacing: 0.4px;
        }

        .rmodal-table tbody tr {
          border-bottom: 1px solid #f1f5f9;
          transition: background 0.15s;
        }
        .rmodal-table tbody tr:hover { background: #fafbfc; }

        .rmodal-table tbody td {
          padding: 8px 10px;
          font-size: 11.5px;
          color: #1e293b;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          vertical-align: middle;
        }

        /* Cell Styles */
        .rmodal-sr { color: #94a3b8; font-size: 11px; font-weight: 500; }
        .rmodal-patient { display: flex; align-items: center; gap: 5px; font-weight: 600; color: #1e293b; }
        .rmodal-patient i { color: #94a3b8; font-size: 12px; flex-shrink: 0; }
        .rmodal-mobile { color: #64748b; font-size: 11px; display: flex; align-items: center; gap: 4px; }
        .rmodal-mobile i { color: #94a3b8; font-size: 10px; }

        .rmodal-uhid {
          display: inline-block; background: #eff6ff; color: #1d4ed8;
          padding: 2px 7px; border-radius: 4px; font-size: 10.5px; font-weight: 600;
          max-width: 100%; overflow: hidden; text-overflow: ellipsis;
        }

        .rmodal-auth-badge {
          display: inline-block; background: #f8fafc; color: #475569;
          border: 1px solid #e2e8f0; padding: 2px 8px; border-radius: 20px;
          font-size: 10.5px; font-weight: 500; max-width: 100%;
          overflow: hidden; text-overflow: ellipsis;
        }
        .rmodal-auth-unknown {
          display: inline-block; background: #fef9c3; color: #854d0e;
          border: 1px solid #fde047; padding: 2px 8px; border-radius: 20px;
          font-size: 10.5px; font-weight: 500;
        }

        .rmodal-pct { font-size: 11px; color: #64748b; font-weight: 500; }

        .rmodal-badge {
          display: inline-block; padding: 3px 9px; border-radius: 20px;
          font-size: 11px; font-weight: 600;
        }
        .rmodal-badge-red { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
        .rmodal-badge-purple { background: #f5f3ff; color: #7c3aed; border: 1px solid #ddd6fe; }

        .rmodal-empty {
          text-align: center; padding: 32px 16px;
          color: #94a3b8; font-size: 12px;
        }
        .rmodal-empty i { font-size: 24px; display: block; margin-bottom: 8px; }
        .rmodal-empty span { display: block; }

        /* Footer */
        .rmodal-footer {
          padding: 12px 20px;
          background: #f8fafc;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-shrink: 0;
        }
        .rmodal-footer-note { font-size: 11px; color: #94a3b8; display: flex; align-items: center; gap: 5px; }
        .rmodal-total { display: flex; flex-direction: column; align-items: flex-end; }
        .rmodal-total span { font-size: 10px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }
        .rmodal-total strong { font-size: 15px; font-weight: 700; line-height: 1.2; }
        .rmodal-total-red strong { color: #dc2626; }
        .rmodal-total-purple strong { color: #7c3aed; }

        /* ════════════════════════════════════════════
           CONVERT TO IPD MODAL
        ════════════════════════════════════════════ */
        .convert-modal { width: 90%; max-width: 480px; background: white; border-radius: 12px; box-shadow: 0 10px 40px rgba(0,0,0,0.2); animation: slideIn 0.3s ease; display: flex; flex-direction: column; }
        .convert-modal .modal-header { background: var(--theme-color, #0088cc); padding: 15px 20px; color: white; display: flex; justify-content: space-between; align-items: center; border-top-left-radius: 12px; border-top-right-radius: 12px; }
        .convert-modal .modal-header h6 { margin: 0; font-size: 15px; font-weight: 600; display: flex; align-items: center; }
        .close-btn { background: transparent; border: none; color: white; font-size: 18px; cursor: pointer; opacity: 0.8; padding: 0; }
        .close-btn:hover { opacity: 1; }
        .convert-modal .modal-body { padding: 20px; overflow: visible !important; }
        .patient-summary { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 15px; margin-bottom: 20px; }
        .patient-summary h6 { color: var(--theme-color, #0088cc); font-size: 14px; margin-bottom: 8px; font-weight: 700; }
        .patient-summary .details { display: flex; flex-wrap: wrap; gap: 15px; font-size: 12px; color: #475569; }
        .patient-summary .details span { display: flex; align-items: center; gap: 5px; }
        .btn-cancel-modal { background: #e2e8f0; color: #475569; border: none; padding: 8px 16px; border-radius: 6px; font-size: 12px; font-weight: 600; cursor: pointer; transition: 0.2s; }
        .btn-cancel-modal:hover { background: #cbd5e1; }
        .btn-confirm-modal { background: var(--theme-color, #0088cc); color: white; border: none; padding: 8px 16px; border-radius: 6px; font-size: 12px; font-weight: 600; cursor: pointer; transition: 0.2s; display: flex; align-items: center; gap: 6px; }
        .btn-confirm-modal:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); box-shadow: 0 4px 10px rgba(0,136,204,0.2); }
        .btn-confirm-modal:disabled { opacity: 0.6; cursor: not-allowed; }

        /* ── Header ── */
        .opd-header { background: var(--theme-color, #0088cc); padding: 8px 16px; display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #00d4aa; }
        .header-left { display: flex; align-items: center; gap: 10px; }
        .header-icon { width: 32px; height: 32px; background: rgba(255,255,255,0.2); border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 16px; color: white; flex-shrink: 0; }
        .header-text h5 { margin: 0; color: white; font-size: 13px; font-weight: 700; line-height: 1.2; }
        .header-text p  { margin: 0; color: rgba(255,255,255,0.85); font-size: 10px; line-height: 1.2; }
        .header-stats { display: flex; gap: 12px; }
        .stat-item { background: rgba(255,255,255,0.2); padding: 4px 10px; border-radius: 6px; display: flex; align-items: center; gap: 6px; }
        .stat-item i { font-size: 16px; color: white; }
        .stat-info { display: flex; flex-direction: column; }
        .stat-value { color: white; font-size: 14px; font-weight: 700; line-height: 1; }
        .stat-label { color: rgba(255,255,255,0.85); font-size: 9px; }

        /* ── Alert ── */
        .alert-custom { display: flex; align-items: center; gap: 8px; padding: 7px 14px; margin: 10px 14px 0; border-radius: 6px; font-size: 11px; background: #fff5f5; color: #d63031; border: 1px solid #ffb8b8; }
        .alert-custom i { font-size: 13px; }
        .alert-close { margin-left: auto; background: none; border: none; font-size: 14px; cursor: pointer; opacity: 0.6; color: inherit; padding: 0; }
        .alert-close:hover { opacity: 1; }

        /* ── Body ── */
        .opd-body { padding: 12px 14px; }

        /* ── Filters ── */
        .filters-section { background: #ffffff; border: 1px solid #e0e6ed; border-radius: 7px; margin-bottom: 12px; }
        .section-header { background: #f0f4f8; padding: 6px 10px; display: flex; align-items: center; gap: 6px; border-bottom: 2px solid var(--theme-color, #0088cc); border-radius: 7px 7px 0 0; }
        .section-header i { font-size: 13px; color: var(--theme-color, #0088cc); }
        .section-header span { font-size: 11px; font-weight: 600; color: #2c3e50; }
        .filters-content { padding: 8px 10px; }
        .date-check-row { display: flex; align-items: center; gap: 6px; margin-bottom: 8px; }
        .date-check-row input[type="checkbox"] { width: 14px; height: 14px; cursor: pointer; }
        .date-check-row label { font-size: 11px; font-weight: 600; color: #2c3e50; cursor: pointer; margin: 0; }
        .filter-row { display: flex; flex-wrap: wrap; gap: 8px; align-items: end; }
        .filter-group { display: flex; flex-direction: column; flex: 1 1 120px; min-width: 100px; }
        .filter-group-flex2 { flex: 2 1 180px; min-width: 150px; }
        .filter-group-rows { flex: 0 1 70px; min-width: 60px; }
        .filter-group-btn  { flex: 0 1 80px; min-width: 70px; }
        .filter-group label { font-size: 10px; font-weight: 600; color: #495057; margin-bottom: 3px; white-space: nowrap; display: flex; align-items: center; gap: 3px; }
        .filter-group label i { color: var(--theme-color, #0088cc); font-size: 11px; }
        .form-control-custom { padding: 3px 7px; border: 1px solid #cbd5e0; border-radius: 4px; font-size: 11px; color: #2c3e50; width: 100%; height: 28px; outline: none; transition: 0.2s; box-sizing: border-box; }
        .form-control-custom:focus { border-color: var(--theme-color, #0088cc); box-shadow: 0 0 0 2px rgba(0,0,0,0.05); }
        .form-control-custom:disabled { background: #f8f9fa; opacity: 0.6; cursor: not-allowed; }
        .btn-refresh { padding: 0 10px; border-radius: 4px; border: none; background: var(--theme-color, #0088cc); color: white; font-size: 11px; font-weight: 600; cursor: pointer; height: 28px; display: flex; align-items: center; justify-content: center; gap: 4px; width: 100%; }
        .btn-refresh:disabled { opacity: 0.6; cursor: not-allowed; }

        /* ── Summary Cards ── */
        .cards-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 12px; }
        .sum-card { position: relative; overflow: hidden; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px; display: flex; align-items: center; gap: 12px; transition: transform 0.2s, box-shadow 0.2s; }
        .sum-card.clickable { cursor: pointer; }
        .sum-card.clickable:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.08); border-color: var(--accent); }
        .sum-glow { position: absolute; bottom: -20px; right: -20px; width: 70px; height: 70px; border-radius: 50%; background: var(--accent); opacity: 0.08; filter: blur(20px); }
        .sum-click-hint { position: absolute; top: 8px; right: 8px; font-size: 11px; color: var(--accent); opacity: 0.5; }
        .sum-icon { font-size: 24px; line-height: 1; }
        .sum-body { display: flex; flex-direction: column; }
        .sum-val { font-size: 16px; font-weight: 800; font-family: 'DM Mono', monospace; line-height: 1.2; }
        .sum-label { font-size: 10px; font-weight: 600; color: #64748b; margin-top: 2px; text-transform: uppercase; }

        /* ── Records Info ── */
        .records-info { display: flex; justify-content: space-between; align-items: center; padding: 5px 10px; background: #f0f4f8; border-radius: 5px; margin-bottom: 8px; border: 1px solid #e0e6ed; font-size: 10px; color: #495057; }
        .info-left { display: flex; align-items: center; gap: 5px; }
        .info-left i { color: var(--theme-color, #0088cc); }
        .page-info { display: flex; align-items: center; gap: 4px; }
        .page-info i { color: var(--theme-color, #0088cc); }

        /* ── Table ── */
        .table-container { background: white; border: 1px solid #e0e6ed; border-radius: 7px; width: 100%; overflow: hidden; }
        .opd-table { width: 100%; border-collapse: collapse; font-size: 10.5px; table-layout: fixed; }
        .opd-table thead { background: var(--theme-color, #0088cc); }
        .opd-table th { padding: 6px 3px; text-align: left; font-weight: 600; color: white; border-bottom: 2px solid #00d4aa; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-size: 10px; }
        .opd-table th:nth-child(1)  { width: 3%;  text-align: center; }
        .opd-table th:nth-child(2)  { width: 3%;  text-align: center; }
        .opd-table th:nth-child(3)  { width: 4%;  }
        .opd-table th:nth-child(4)  { width: 9%;  }
        .opd-table th:nth-child(5)  { width: 14%; }
        .opd-table th:nth-child(6)  { width: 7%;  }
        .opd-table th:nth-child(7)  { width: 11%; }
        .opd-table th:nth-child(8)  { width: 9%;  }
        .opd-table th:nth-child(9)  { width: 7%;  }
        .opd-table th:nth-child(10) { width: 12%; }
        .opd-table th:nth-child(11) { width: 9%;  }
        .opd-table th:nth-child(12) { width: 6%;  text-align: center; }
        .opd-table th:nth-child(13) { width: 6%;  text-align: center; }
        .opd-table tbody tr { border-bottom: 1px solid #e8ecf0; }
        .opd-table tbody tr:hover { background: #f5f8fb; }
        .opd-table tbody tr.row-converted { background: #d4edda; }
        .opd-table td { padding: 5px 3px; color: #2c3e50; vertical-align: middle; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-size: 10.5px; }

        /* Badges */
        .id-badge, .uhid-badge { background: #e7f3ff; color: var(--theme-color, #0088cc); padding: 1px 5px; border-radius: 3px; font-weight: 600; font-size: 9.5px; display: inline-block; max-width: 100%; overflow: hidden; text-overflow: ellipsis; }
        .date-badge { background: #f0f4f8; color: #495057; padding: 1px 5px; border-radius: 3px; font-size: 9.5px; font-weight: 500; }
        .mobile-badge { display: inline-flex; align-items: center; gap: 3px; font-size: 10px; color: #2c3e50; }
        .mobile-badge i { font-size: 9px; color: var(--theme-color, #0088cc); }
        .age-gender { font-size: 10px; }
        .patient-name, .doctor-name { font-weight: 500; display: flex; align-items: center; gap: 3px; overflow: hidden; text-overflow: ellipsis; font-size: 10.5px; }
        .patient-name i, .doctor-name i { font-size: 10px; color: var(--theme-color, #0088cc); flex-shrink: 0; }
        .address-cell { color: #666; font-size: 10px; }
        .badge-converted { background: #d4edda; color: #155724; padding: 1px 5px; border-radius: 50px; font-size: 9px; font-weight: 600; display: inline-flex; align-items: center; gap: 3px; }

        /* Action Buttons */
        .btn-icon { width: 22px; height: 22px; border-radius: 4px; border: none; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; font-size: 11px; }
        .btn-prescription { background: #28a745; color: white; }
        .btn-regslip      { background: var(--theme-color, #0088cc); color: white; }
        .btn-convert      { background: #FF6B00; color: white; }
        .btn-edit         { background: #e7f3ff; color: var(--theme-color, #0088cc); }
        .btn-delete       { background: #fee; color: #dc3545; }
        .action-buttons   { display: flex; gap: 3px; justify-content: center; }
        .action-cell      { text-align: center; }

        /* Loading / Empty */
        .loading-state, .empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 30px; color: #6c757d; }
        .loading-state p, .empty-state p { font-size: 11px; margin: 6px 0 0; }
        .empty-state i  { font-size: 32px; color: #cbd5e0; }
        .empty-state h6 { font-size: 12px; margin: 6px 0 2px; color: #495057; }
        .spinner { width: 28px; height: 28px; border: 3px solid #e0e6ed; border-top-color: var(--theme-color, #0088cc); border-radius: 50%; animation: spin 0.8s linear infinite; }

        /* ── Pagination ── */
        .pagination-section { display: flex; justify-content: center; gap: 5px; margin-top: 8px; padding-top: 8px; border-top: 1px solid #e0e6ed; flex-wrap: wrap; }
        .pagination-btn { padding: 3px 9px; border-radius: 4px; border: 1px solid #cbd5e0; background: white; color: #2c3e50; font-size: 10px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 3px; }
        .pagination-btn:hover { background: #f0f4f8; }
        .pagination-btn.active { background: var(--theme-color, #0088cc); color: white; border-color: var(--theme-color, #0088cc); }
        .pagination-btn-nav { color: #495057; }

        /* ── Responsive ── */
        @media (max-width: 1200px) { .cards-grid { grid-template-columns: repeat(3, 1fr); } }
        @media (max-width: 1024px) {
          .table-container { overflow-x: auto; }
          .opd-table { min-width: 900px; table-layout: fixed; }
        }
        @media (max-width: 768px) {
          .cards-grid { grid-template-columns: repeat(2, 1fr); }
          .opd-body { padding: 8px 10px; }
          .opd-header { padding: 7px 12px; }
          .header-stats { display: none; }
          .filter-row { gap: 6px; }
          .filter-group { flex: 1 1 100px; }
          .filter-group-flex2 { flex: 1 1 100%; }
          .report-modal { max-width: 95%; }
        }
        @media (max-width: 480px) {
          .cards-grid { grid-template-columns: 1fr; }
          .filter-group { flex: 1 1 100%; }
          .filter-group-rows, .filter-group-btn { flex: 1 1 45%; }
        }
      `}</style>
    </div>
  );
};

export default OpdList;